import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Phone, ArrowRight, Handshake } from 'lucide-react';
import Seo from '../components/Seo';
import FAQAccordion from '../components/FAQAccordion';
import PaymentOptions from '../components/PaymentOptions';

const FAQPage = () => {
  const allFAQs = [
    {
      question: "How do I schedule a ride?",
      answer: "Call our dispatch line at (832) 925-2128 or use our online booking form to request transportation. Our team is available during operating hours: Monday–Saturday 6:00 AM – 7:00 PM and Sunday 12:00 PM – 5:00 PM."
    },
    {
      question: "What areas do you serve?",
      answer: "We serve Greater Houston — Harris, Fort Bend, Brazoria, Montgomery, and Galveston Counties — including Richmond, Sugar Land, Katy, Missouri City, Stafford, Rosenberg, and Pearland. Extended-distance service may be available with advance authorization."
    },
    {
      question: "Do you provide wheelchair transportation?",
      answer: "Yes. We operate wheelchair-accessible vehicles with proper wheelchair securement for medically stable passengers. Drivers assist passengers during transport. Please share the wheelchair type, weight, and mobility level when booking so dispatch can confirm we are able to serve the trip."
    },
    {
      question: "Can I book same-day transportation?",
      answer: "Same-day service may be available based on scheduling, vehicle, and driver availability. Dispatch confirms the pickup time when accepting a booking. We recommend calling dispatch directly at (832) 925-2128 for urgent same-day requests."
    },
    {
      question: "Do you offer dialysis transportation?",
      answer: "Yes. We provide recurring and scheduled dialysis transportation with dependable coordination. Standing weekly schedules are supported."
    },
    {
      question: "Do you support Workers' Compensation transportation?",
      answer: "Yes. MoveraCare coordinates non-emergency transportation for injured workers to authorized medical, therapy, IME, FCE, and diagnostic appointments. Authorization determinations are made by the applicable payer, adjuster, case manager, or TPA."
    },
    {
      question: "How is payment handled?",
      answer: "Payment arrangements and any required authorization are confirmed before the trip. Private-pay and authorized partner trips may be available. Payment options may include Zelle and check; ACH bank transfer instructions will be available after setup. The applicable payment method, amount, and due date will be confirmed for your trip or shown on your invoice. For Zelle, use moveracareservices@gmail.com only after confirming the recipient with MoveraCare dispatch. Make checks payable to MoveraCare Services; ask dispatch for delivery instructions."
    },
    {
      question: "Are you licensed and insured?",
      answer: "Yes. MoveraCare maintains required licensing and commercial insurance for non-emergency medical transportation operations. Documentation is available upon request through Provider Relations."
    },
    {
      question: "Can healthcare facilities partner with you?",
      answer: "Yes. Facilities across Greater Houston — hospitals, SNFs, dialysis centers, clinics, TPAs, NEMT brokers, and Workers' Compensation programs — may contact us about scheduled non-emergency transportation. Visit our Healthcare Partners page or Procurement Portal to learn more."
    },
    {
      question: "Do you offer hospital discharge transportation?",
      answer: "Yes. We provide same-day and scheduled discharge transportation from hospitals to home, SNF, rehabilitation, or assisted living for medically stable patients. Dispatch confirms the pickup window when the trip is accepted."
    },
    {
      question: "Can family members ride along?",
      answer: "A family member or authorized companion may ride with the passenger, subject to seating availability, vehicle capacity, and advance notice. Please mention companion information when scheduling."
    },
    {
      question: "Do you offer recurring weekly rides?",
      answer: "Yes. We support recurring transportation for dialysis, physical therapy, rehabilitation, workers' compensation treatment, and other ongoing medical appointments."
    },
    {
      question: "Do you provide transportation for nursing home residents?",
      answer: "Yes. Skilled nursing facilities, assisted living communities, and long-term care organizations across Greater Houston may contact us about scheduled non-emergency transportation, subject to facility agreements."
    },
    {
      question: "How far in advance should I schedule?",
      answer: "For routine appointments we recommend 24–48 hours notice. Wheelchair, oxygen-capable, long-distance, discharge, and wait-and-return trips benefit from earlier scheduling. Same-day requests may be considered based on availability."
    },
    {
      question: "What are your operating hours?",
      answer: "MoveraCare operates Monday–Saturday 6:00 AM – 7:00 PM and Sunday 12:00 PM – 5:00 PM. Trips outside standard operating hours may be considered by advance arrangement."
    },
    {
      question: "Can facilities receive monthly billing?",
      answer: "Yes. Healthcare facilities may establish account arrangements with billing terms that fit their internal accounting workflows. Contact Provider Relations to review options."
    },
    {
      question: "How do I request a transportation quote?",
      answer: "Contact dispatch at (832) 925-2128 or dispatch@moveracaremobility.com with pickup, destination, mobility needs, and schedule. We'll review the request and confirm availability and pricing."
    }
  ];

  const FAQ_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: allFAQs.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen pt-20">
      <Seo
        title="MoveraCare Mobility FAQ | Houston NEMT Questions Answered"
        description="Answers about scheduling, wheelchair transportation, workers' compensation, dialysis, hospital discharge, facility partnerships, and NEMT services across Greater Houston."
        path="/faq"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'FAQ', url: '/faq' }]}
        schema={FAQ_SCHEMA}
      />
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-[#004C97] to-[#003D7A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-100 leading-relaxed">
            Answers about scheduling, pricing, wheelchair transportation, facility partnerships, and medical ride services across Houston and surrounding areas.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FAQAccordion faqs={allFAQs} defaultOpen={0} />

          {/* Contextual in-content links — takes readers from a matching FAQ
              answer to the dedicated service page. */}
          <div className="mt-10 pt-8 border-t border-gray-200" data-testid="faq-related-services">
            <p className="text-sm uppercase tracking-widest text-[#F9A825] font-semibold mb-3">Learn more</p>
            <ul className="space-y-2 text-gray-700 leading-relaxed">
              <li>
                For details on wheelchair-accessible vehicles, securement and passenger assistance, see our{' '}
                <Link
                  to="/services/wheelchair-transportation"
                  className="text-[#004C97] font-semibold underline hover:text-[#F9A825]"
                  data-testid="faq-link-wheelchair"
                >
                  wheelchair transportation service page
                </Link>.
              </li>
              <li>
                For same-day discharge coordination with hospitals, SNFs and rehab, see{' '}
                <Link
                  to="/services/hospital-discharge"
                  className="text-[#004C97] font-semibold underline hover:text-[#F9A825]"
                  data-testid="faq-link-hospital-discharge"
                >
                  hospital discharge transportation
                </Link>.
              </li>
              <li>
                Families and case managers scheduling standing weekly rides can review{' '}
                <Link
                  to="/services/recurring-medical-transportation"
                  className="text-[#004C97] font-semibold underline hover:text-[#F9A825]"
                  data-testid="faq-link-recurring"
                >
                  recurring medical transportation
                </Link>{' '}
                and{' '}
                <Link
                  to="/services/dialysis-transportation"
                  className="text-[#004C97] font-semibold underline hover:text-[#F9A825]"
                  data-testid="faq-link-dialysis"
                >
                  dialysis transportation
                </Link>.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Payment Options */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <PaymentOptions variant="full" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#004C97] mb-6">
            Still Have Questions?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Our dispatch team is ready to assist you with booking, pricing, and partnership inquiries.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book">
              <Button size="lg" className="bg-[#F9A825] text-white hover:bg-[#E08E1E] font-semibold text-lg px-10">
                Book a Ride
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/partners">
              <Button size="lg" variant="outline" className="border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white font-semibold text-lg px-10">
                <Handshake className="mr-2 w-5 h-5" />
                Partner With Us
              </Button>
            </Link>
            <a href="tel:+18329252128">
              <Button size="lg" variant="outline" className="border-2 border-[#F9A825] text-[#F9A825] hover:bg-[#F9A825] hover:text-white font-semibold text-lg px-10">
                <Phone className="mr-2 w-5 h-5" />
                Call Dispatch
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Service Areas Callout */}
      <section className="py-12 bg-[#004C97]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#F9A825] text-lg font-semibold mb-2">
            Proudly Serving
          </p>
          <p className="text-white text-lg">
            Houston • Sugar Land • Katy • Richmond • Missouri City • Fort Bend County • Harris County
          </p>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
